/**
 * ColorPickerTabs  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useState } from "react";
import { BEM } from "@adapptio/lib-components";
import { ITabsProps, Tabs, TABS_STYLE } from "../Tabs";
import { TABBED_CONTAINER_TYPE, TabbedContainer } from "../TabbedContainer";
import { ColorPickerPalette } from "./ColorPickerPalette";
import { ColorPickerSpectrum } from "./ColorPickerSpectrum";
import { ColorPickerGrid } from "./ColorPickerGrid";
import { useColorPickerContext } from "./ColorPickerContext";
import { colorGridData } from "./colorGridData";
import { colorEquals, getColorUtils } from "./colorUtils";

export interface IColorPickerTabs {
	className?: string;
	defaultTab?: typeof tabs[number]["id"];
}

const tabs: ITabsProps["items"] = [
	{
		id: "palette",
		label: "Palette"
	},
	{
		id: "grid",
		label: "Grid"
	},
	{
		id: "spectrum",
		label: "Spectrum"
	}
];

export const ColorPickerTabs: React.FC<IColorPickerTabs> = ({ className, defaultTab }) => {
	const baseClass = BEM("edt-color-picker-tabs");
	const { valueFromPalette, value } = useColorPickerContext();
	const [ activeTab, setActiveTab ] = useState<typeof tabs[number]["id"]>(() => {
		if (valueFromPalette || !value) {
			return "palette";
		}

		const matchesColorGrid = colorGridData.some((colorGridRow) =>
			colorGridRow?.some((color) => colorEquals(color, value))
		);

		if (matchesColorGrid) {
			return "grid";
		}

		return "spectrum";
	});

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()}>
			<Tabs
				className={baseClass.element("tabs").toClassName()}
				items={tabs}
				selectedId={activeTab}
				onSelect={setActiveTab}
				style={TABS_STYLE.COMPACT}
			/>
			<TabbedContainer
				className={baseClass.element("tab-container").toClassName()}
				style={TABBED_CONTAINER_TYPE.SLIDE}
				items={[
					{
						id: "palette",
						render: () => (
							<div className={baseClass.element("tab-content").toClassName()}>
								<ColorPickerPalette />
							</div>
						)
					},
					{
						id: "grid",
						render: () => (
							<div className={baseClass.element("tab-content").toClassName()}>
								<ColorPickerGrid />
							</div>
						)
					},
					{
						id: "spectrum",
						render: () => (
							<div className={baseClass.element("tab-content").toClassName()}>
								<ColorPickerSpectrum />
							</div>
						)
					}
				]}
				selectedId={activeTab}
			/>
		</div>
	);
};

ColorPickerTabs.defaultProps = {
	defaultTab: "spectrum"
};
